import React, { useEffect, useState, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import config from '../Config';
import Loader from './Loader';
import { useNavigate, useLocation } from 'react-router-dom';
import { HandleResponse } from '../functions/HandleResponse';
import { authFetch } from '../functions/authFetch';
import { downloadBuildLog, formatLogSize } from '../functions/jobLog';
import { useSingleFlight } from '../hooks/useSingleFlight';
import './pages.css';

const ACTIVE_STATUSES = ['queued', 'running'];
const POLL_INTERVAL_MS = 5000;

const STATUS_COLOR = {
  queued: '#f59e0b',
  running: '#3b82f6',
  success: '#22c55e',
  failed: '#ef4444',
  cancelled: '#9ca3af',
};

const isActive = (status) => ACTIVE_STATUSES.includes(status);

// Where the build context came from, for the row tooltip. Automation builds are the
// ones a user did not start by hand, so knowing the repo and branch matters most there.
const describeSource = (build) => {
  const source = build.source || {};
  if (source.type !== 'github' || !source.repo) return '';
  return source.branch ? `${source.repo} @ ${source.branch}` : source.repo;
};

const MCRPushHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [builds, setBuilds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [logContent, setLogContent] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [totalBytes, setTotalBytes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [minimizedBuilds, setMinimizedBuilds] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const runLogFetch = useSingleFlight();
  const autoSelectDone = useRef(false);
  const bodyRef = useRef(null);

  const token = Cookies.get('runner_app');

  // ── list ───────────────────────────────────────────────────────────────────

  const fetchBuilds = useCallback(async (signal) => {
    const response = await authFetch(`${config.RUNNER_API}/v1/cdci/builds`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    await HandleResponse(response, navigate);
    if (!response.ok) return null;
    const data = await response.json();
    return Array.isArray(data.builds) ? data.builds : [];
  }, [navigate, token]);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const ac = new AbortController();
    let cancelled = false;

    const load = async () => {
      setListLoading(true);
      try {
        const result = await fetchBuilds(ac.signal);
        if (cancelled || !result) return;
        setBuilds(result);
      } catch (err) {
        if (err?.name === 'AbortError') return;
      } finally {
        if (!cancelled && !ac.signal.aborted) setListLoading(false);
      }
    };
    load();
    return () => { cancelled = true; ac.abort(); };
  }, [navigate, token, fetchBuilds]);

  // Refresh the list while anything is still building, so a build started elsewhere
  // (a GitHub push, another tab) appears without a manual reload.
  const hasActive = builds.some(b => isActive(b.status));

  useEffect(() => {
    if (!hasActive || !token) return undefined;
    const ac = new AbortController();
    const id = setInterval(async () => {
      try {
        const fresh = await fetchBuilds(ac.signal);
        if (fresh) setBuilds(fresh);
      } catch { /* transient; the next tick retries */ }
    }, POLL_INTERVAL_MS);
    return () => { clearInterval(id); ac.abort(); };
  }, [hasActive, fetchBuilds, token]);

  // ── log ────────────────────────────────────────────────────────────────────

  const applyLogText = useCallback((raw) => {
    const text = (typeof raw === 'string' ? raw : String(raw || '')).replace(/\\n/g, '\n');
    setLogContent(text.split('\n'));
  }, []);

  const fetchBuildLog = useCallback(async (build, quiet = false) => {
    if (!token) return;
    if (!quiet) { setLoading(true); setLogContent(['Loading…']); }
    setSelected(build);
    try {
      const url = `${config.RUNNER_API}/v1/cdci/build-log/${encodeURIComponent(build.buildId)}`;
      const response = await authFetch(url, { headers: { Authorization: `Bearer ${token}` } });
      await HandleResponse(response, navigate);
      if (response.ok) {
        const data = await response.json();
        applyLogText(data.log);
        setTruncated(Boolean(data.truncated));
        setTotalBytes(Number(data.totalBytes) || 0);
      } else {
        const err = await response.json().catch(() => ({}));
        setLogContent([err.detail || `Failed to load log for ${build.imageName}`]);
        setTruncated(false);
        setTotalBytes(0);
      }
    } catch (err) {
      setLogContent([err.message || 'Error loading log.']);
    }
    if (!quiet) setLoading(false);
  }, [navigate, token, applyLogText]);

  // Follow a running build's output. The builder pushes its log tail every few
  // seconds, so re-reading on the same cadence is as live as it gets.
  const selectedBuild = selected?.buildId
    ? builds.find(b => b.buildId === selected.buildId) || selected
    : selected;
  const followLog = Boolean(selectedBuild?.buildId && isActive(selectedBuild.status));

  useEffect(() => {
    if (!followLog || !selectedBuild) return undefined;
    const id = setInterval(() => { fetchBuildLog(selectedBuild, true); }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [followLog, selectedBuild, fetchBuildLog]);

  // Keep the newest output in view while a build is still writing to it.
  useEffect(() => {
    if (followLog && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [logContent, followLog]);

  // Auto-open the log when arriving from Automation Management with a pre-selected image
  useEffect(() => {
    const target = location.state?.autoSelectImage;
    if (!target || autoSelectDone.current || listLoading) return;
    const match = builds.find(b => b.imageName === target);
    if (match) {
      autoSelectDone.current = true;
      runLogFetch(() => fetchBuildLog(match));
    }
  }, [builds, listLoading, location.state, fetchBuildLog, runLogFetch]);

  // ── actions ────────────────────────────────────────────────────────────────

  const keyOf = (build) => build.buildId || build.imageName;

  const handleBuildClick = (build) => {
    const key = keyOf(build);
    if (minimizedBuilds[key]) {
      setLogContent(minimizedBuilds[key]);
      setSelected(build);
      setMinimizedBuilds(prev => { const n = { ...prev }; delete n[key]; return n; });
    } else {
      runLogFetch(() => fetchBuildLog(build));
    }
  };

  const handleClose = () => {
    setLogContent(null);
    setSelected(null);
    setIsFullscreen(false);
    setTruncated(false);
    setTotalBytes(0);
    setDownloadError(null);
  };

  const handleMinimize = () => {
    if (selected && logContent) {
      setMinimizedBuilds(prev => ({ ...prev, [keyOf(selected)]: logContent }));
      setLogContent(null);
      setSelected(null);
    }
  };

  const handleExpandToggle = () => setIsFullscreen(f => !f);

  const handleCancel = async () => {
    if (!selectedBuild?.buildId || cancelling) return;
    setCancelling(true);
    try {
      const response = await authFetch(
        `${config.RUNNER_API}/v1/cdci/cancel-build/${encodeURIComponent(selectedBuild.buildId)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      await HandleResponse(response, navigate);
      const fresh = await fetchBuilds();
      if (fresh) setBuilds(fresh);
    } catch { /* the list refresh on the next poll will show the real state */ }
    setCancelling(false);
  };

  const handleDownload = async () => {
    if (!selectedBuild?.buildId || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadBuildLog(selectedBuild.buildId);
    } catch (err) {
      setDownloadError(err.message || 'Could not download the log.');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (raw) => {
    if (!raw) return '–';
    try {
      const d = new Date(raw);
      return isNaN(d.getTime()) ? raw : d.toLocaleString();
    } catch { return raw; }
  };

  const viewerTitle = () => {
    if (!selectedBuild) return 'Select a build to view log';
    const status = selectedBuild.status ? ` — ${selectedBuild.status}` : '';
    const source = describeSource(selectedBuild);
    return `Build log: ${selectedBuild.imageName}${status}${source ? ` (${source})` : ''}`;
  };

  return (
    <div className="lv-container">
      <Loader show={loading || listLoading} />

      {/* ── Left sidebar ── */}
      <div className="lv-sidebar">
        <div className="lv-sidebar-header">
          <span className="lv-sidebar-title">Build History</span>
          <span className="lv-sidebar-count">{builds.length}</span>
        </div>

        <ul className="lv-list">
          {builds.length > 0 ? builds.map((item, i) => {
            const key = keyOf(item);
            const active = selected && keyOf(selected) === key;
            return (
              <li
                key={`${key}-${i}`}
                ref={el => { if (el && active) el.scrollIntoView({ block: 'nearest' }); }}
                className={`lv-list-item ${active ? 'active' : ''} ${minimizedBuilds[key] ? 'minimized' : ''}`}
                onClick={() => handleBuildClick(item)}
                title={item.failureReason || describeSource(item) || item.status || ''}
              >
                <span
                  className="lv-status-dot"
                  style={{ background: STATUS_COLOR[item.status] || '#22c55e' }}
                />
                <span className="lv-item-id">{item.imageName}</span>
                <span className="lv-time">{formatDate(item.createdAt)}</span>
              </li>
            );
          }) : (
            <li className="lv-empty">No builds yet</li>
          )}
        </ul>
      </div>

      {/* ── Log viewer ── */}
      <div className={`lv-viewer${isFullscreen ? ' lv-viewer--fullscreen' : ''}`}>
        <div className="lv-viewer-header">
          <div className="lv-viewer-dots">
            <button className="lv-close-btn" title="Close log" onClick={handleClose} />
            <button className="lv-minimize-btn" title="Minimize" onClick={handleMinimize} />
            <button className="lv-expand-btn" title={isFullscreen ? 'Restore' : 'Expand'} onClick={handleExpandToggle} />
          </div>
          <span className="lv-viewer-title">{viewerTitle()}</span>
          {logContent && (
            <span className="lv-line-count">{logContent.length} lines</span>
          )}
          {logContent && selectedBuild?.buildId && (
            <button
              className="lv-download-btn"
              onClick={handleDownload}
              disabled={downloading}
              title="Download the full log as a .log file"
            >
              {downloading ? 'Downloading…' : '⬇ Download Log'}
            </button>
          )}
          {selectedBuild?.buildId && isActive(selectedBuild.status) && (
            <button className="lv-delete-btn" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling…' : 'Cancel build'}
            </button>
          )}
        </div>
        <div className="lv-viewer-body" ref={bodyRef}>
          {logContent ? (
            <>
              {downloadError && (
                <div className="lv-log-notice lv-log-notice--error">{downloadError}</div>
              )}
              {truncated && (
                <div className="lv-log-notice">
                  This log is {formatLogSize(totalBytes)} — too large to show in full, so only
                  the end is displayed. <strong>Download Log</strong> to view all of it.
                </div>
              )}
              {logContent.map((line, i) => (
                <div key={i} className={`lv-line ${line.trim().startsWith('=>') ? 'lv-line-step' : ''}`}>
                  {line || '\u00A0'}
                </div>
              ))}
            </>
          ) : (
            <span className="lv-placeholder">← Select a build from the list</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MCRPushHistory;
